import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SecondaryButton from "../../../DesignSystem/Buttons/SecondaryButton";
import CheckboxMultiSelect from "../../../DesignSystem/Dropdowns/CheckboxMultiSelect";
import CustomSelect from "../../../DesignSystem/Dropdowns/CustomSelect";
import { PostCard } from "../HelperFunctions/HelperFunctions";
import ProgressHistory from "../../../Marketing/MarketingDashabord/components/ProgressHistory";
import {
  formatAdviserCatchUpPersonName,
  resolveAssignedStaffEmails,
  getAdviserCatchUpSelectOptions,
  resolveAdviserCatchUpPrimaryEmail,
} from "./adviserCatchUpConfig";
import {
  addCatchUpEntry,
  saveAdviserCatchUpPointOfContact,
  subscribeAdviserCatchUpData,
  subscribeCatchUps,
} from "./adviserCatchUpService";
import { auth } from "../../../../backend/firebaseConfig";

const SummaryField = ({ label, children, full = false }) => (
  <div className={`smRow ${full ? "adviserCatchUpSummaryFull" : ""}`}>
    <div className="smRowLabel FP_Body text-grey">{label}</div>
    <div className="smRowValue FP_Body text-black adviserCatchUpBlankable">
      {children || "-"}
    </div>
  </div>
);

const StaffPills = ({ emails = [], primaryEmail = "" }) => {
  if (!emails.length) return null;

  return (
    <div className="adviserCatchUpPills">
      {emails.map((email) => (
        <span
          className={`adviserCatchUpPill ${
            email === primaryEmail ? "adviserCatchUpPill--Primary" : ""
          }`}
          key={email}
          title={email}
        >
          {formatAdviserCatchUpPersonName(email) || email}
          {email === primaryEmail ? (
            <b className="FP_Help_Text_Semibold">Primary</b>
          ) : null}
        </span>
      ))}
    </div>
  );
};

const formatStoredPersonName = (name = "", email = "") => {
  const displayName = String(name || "").trim();
  if (displayName && !displayName.includes("@")) return displayName;

  return formatAdviserCatchUpPersonName(email || displayName) || displayName;
};

const catchUpsToHistoryItems = (catchUps = []) =>
  catchUps.map((entry) => ({
    id: entry.id,
    label: "Catch Up logged",
    notes: "",
    description: entry.notes || "",
    tone: "blue",
    createdAtMs: entry.catchUpAt || entry.createdAt || "",
    userDisplayName: formatStoredPersonName(entry.addedBy, entry.addedByEmail),
    userEmail: entry.addedByEmail || "",
  }));

const AdviserCatchUpPanel = ({
  adviserId,
  adviserDoc = {},
  canEdit,
  canLogCatchUp: canLogCatchUpOverride = false,
  onEdit,
  saveRequestKey = 0,
  onSavingChange,
  onSaved,
  showSummary = true,
}) => {
  const options = useMemo(() => getAdviserCatchUpSelectOptions(), []);
  const [catchUpData, setCatchUpData] = useState({});
  const resolvedEmail = resolveAdviserCatchUpPrimaryEmail(catchUpData, adviserDoc);
  const resolvedAssignedEmails = useMemo(
    () => resolveAssignedStaffEmails(catchUpData, adviserDoc),
    [adviserDoc, catchUpData],
  );
  const initialSelectedEmails = resolvedAssignedEmails.length
    ? resolvedAssignedEmails
    : resolvedEmail
      ? [resolvedEmail]
      : [];
  const [selectedEmails, setSelectedEmails] = useState(initialSelectedEmails);
  const [primaryEmail, setPrimaryEmail] = useState(resolvedEmail);
  const [catchUpAt, setCatchUpAt] = useState("");
  const [notes, setNotes] = useState("");
  const [catchUps, setCatchUps] = useState([]);
  const [error, setError] = useState("");
  const lastHandledSaveRequestKey = useRef(saveRequestKey);
  const primaryContactOptions = useMemo(
    () =>
      selectedEmails.map((email) => ({
        id: email,
        name: formatAdviserCatchUpPersonName(email) || email,
      })),
    [selectedEmails],
  );
  const currentUserEmail = String(auth.currentUser?.email || "")
    .trim()
    .toLowerCase();
  const canLogCatchUp =
    canEdit &&
    (canLogCatchUpOverride || resolvedAssignedEmails.includes(currentUserEmail));

  useEffect(() => {
    const nextAssignedEmails = resolveAssignedStaffEmails(catchUpData, adviserDoc);
    const nextPrimaryEmail = resolveAdviserCatchUpPrimaryEmail(catchUpData, adviserDoc);
    const nextSelectedEmails = nextAssignedEmails.length
      ? nextAssignedEmails
      : nextPrimaryEmail
        ? [nextPrimaryEmail]
        : [];
    setSelectedEmails(nextSelectedEmails);
    setPrimaryEmail(
      nextSelectedEmails.includes(nextPrimaryEmail)
        ? nextPrimaryEmail
        : nextSelectedEmails[0] || nextPrimaryEmail,
    );
  }, [adviserDoc, catchUpData]);

  useEffect(
    () => subscribeAdviserCatchUpData(adviserId, setCatchUpData),
    [adviserId],
  );

  useEffect(() => subscribeCatchUps(adviserId, setCatchUps), [adviserId]);

  const historyItems = useMemo(() => catchUpsToHistoryItems(catchUps), [catchUps]);

  const handleSave = useCallback(async () => {
    try {
      onSavingChange?.(true);
      setError("");
      await saveAdviserCatchUpPointOfContact(adviserId, selectedEmails, primaryEmail);

      if (canLogCatchUp && (catchUpAt || notes.trim())) {
        await addCatchUpEntry(adviserId, { catchUpAt, notes });
        setCatchUpAt("");
        setNotes("");
      }

      onSaved?.();
    } catch (saveError) {
      setError(saveError.message || "Unable to save adviser Catch Up details.");
    } finally {
      onSavingChange?.(false);
    }
  }, [
    adviserId,
    canLogCatchUp,
    catchUpAt,
    notes,
    onSaved,
    onSavingChange,
    primaryEmail,
    selectedEmails,
  ]);

  useEffect(() => {
    if (!canEdit || saveRequestKey === lastHandledSaveRequestKey.current) {
      return;
    }

    lastHandledSaveRequestKey.current = saveRequestKey;
    handleSave();
  }, [canEdit, handleSave, saveRequestKey]);

  if (!options.length) {
    return (
      <PostCard title="Adviser Catch Up">
        <p className="Content-Small muted">
          Configure adviser Catch Up points of contact to use this feature.
        </p>
      </PostCard>
    );
  }

  const formContent = canEdit ? (
    <PostCard title="Adviser Catch Up">
      {canLogCatchUp ? (
        <div className="adviserCatchUpForm">
          <div className="adviserCatchUpNotes">
            <CheckboxMultiSelect
              label="Points of contact assigned to this adviser"
              required
              value={selectedEmails}
              options={options}
              placeholder="Select points of contact"
              helperText="Select one or more points of contact"
              onChange={(nextEmails) => {
                setSelectedEmails(nextEmails);
                if (!nextEmails.includes(primaryEmail)) {
                  setPrimaryEmail(nextEmails[0] || "");
                }
              }}
            />
          </div>
          <label className="adviserCatchUpField Content-Small">
            <CustomSelect
              label="Primary point of contact"
              options={primaryContactOptions}
              value={primaryEmail}
              onChange={(event) => setPrimaryEmail(event.target.value)}
              placeholder="Select primary point of contact"
              disabled={!selectedEmails.length}
              clearable={false}
              searchable
            />
          </label>
          <label className="adviserCatchUpField Content-Small">
            Catch Up date and time
            <input
              type="datetime-local"
              value={catchUpAt}
              onChange={(event) => setCatchUpAt(event.target.value)}
            />
          </label>
          <label className="adviserCatchUpField adviserCatchUpNotes Content-Small">
            Notes
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={10}
              placeholder="Add Catch Up notes"
            />
          </label>
          {error ? (
            <div className="Content-Small adviserCatchUpError" role="alert">
              {error}
            </div>
          ) : null}
        </div>
      ) : (
        <p className="Content-Small muted">
          Only assigned points of contact or administrators can log Catch Up details.
        </p>
      )}
      <div className="adviserCatchUpHistory">
        <ProgressHistory
          items={historyItems}
          title=""
          ariaLabel="Adviser Catch Up history"
          emptyMessage="No Catch Up entries logged yet."
          showDuration
          tooltipPosition="left"
        />
      </div>
    </PostCard>
  ) : null;

  if (canEdit) {
    return formContent;
  }

  return (
    <div className="smFeed">
      <div className="smEditActions">
        <div>
          <div className="FP_Body_Semibold">Adviser Catch Up</div>
          <p className="Content-Small muted">
            Points of contact and Catch Up history.
          </p>
        </div>
        {onEdit ? <SecondaryButton label="Edit Details" onClick={onEdit} /> : null}
      </div>

      <PostCard title="Points of contact">
        {showSummary ? (
          <div className="adviserCatchUpSummary">
            <SummaryField label="Assigned staff" full>
              <StaffPills emails={resolvedAssignedEmails} primaryEmail={resolvedEmail} />
            </SummaryField>
          </div>
        ) : null}
      </PostCard>

      <PostCard title="Catch Up history">
        <div className="adviserCatchUpHistory adviserCatchUpHistory--NoDivider">
          <ProgressHistory
            items={historyItems}
            title=""
            ariaLabel="Adviser Catch Up history"
            emptyMessage="No Catch Up entries logged yet."
            showDuration
            tooltipPosition="left"
          />
        </div>
      </PostCard>
    </div>
  );
};

export default AdviserCatchUpPanel;
